import { axiosAzureAPI } from './axiosService';
import { AxiosError } from 'axios';

export type PostType = 'event' | 'news';

export interface EventDate {
  eventDate: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
  sortOrder: number;
}

export interface PostImage {
  imageUrl: string;
  originalUrl?: string;
  mediaType: string;
  sortOrder: number;
  caption?: string;
}

export interface PostTranslation {
  title: string;
  summary?: string;
  content?: string;
  keywords?: string[];
}

export interface CreatePostPayload {
  type: PostType;
  title: string;
  summary?: string;
  content?: string;
  categoryId?: string;
  keywords?: string[];
  isUrgent: boolean;
  eventLocation?: string; // Only for events
  publishedAt: string;
  sourceUrl?: string;
  eventDates?: EventDate[]; // Only for events
  images?: PostImage[];
  translations?: Record<string, PostTranslation>;
}

export interface UpdatePostPayload extends Partial<CreatePostPayload> {
  id: string;
}

// Keep legacy names for backward compatibility
export type EventImage = PostImage;
export type EventTranslation = PostTranslation;
export type CreateEventPayload = CreatePostPayload;
export type UpdateEventPayload = UpdatePostPayload;

export class FeedPostService {
  private axiosInstance = axiosAzureAPI;

  async getPosts(
    postType: PostType,
    pageIndex: number,
    pageSize: number,
    sortColumn: string,
    sortDirection: string,
    searchTerm: string,
    includeDuplicates: boolean = true,
  ) {
    try {
      const params = new URLSearchParams({
        type: postType,
        page: (pageIndex + 1).toString(),
        pageSize: pageSize.toString(),
        includeDuplicates: includeDuplicates.toString(),
      });

      if (searchTerm && searchTerm.trim() !== '') {
        params.append('search', searchTerm);
      }

      const { data } = await this.axiosInstance.get(`/api/feed/posts?${params.toString()}`);
      return {
        data: data.posts || [],
        totalPages: data.totalPages || 0,
      };
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
      return { data: [], totalPages: 0 };
    }
  }

  async markAsDuplicate(postId: string, duplicateOfId: string) {
    try {
      const { data } = await this.axiosInstance.post(
        `/api/feed/posts/${postId}/mark-duplicate`,
        { duplicateOfId },
      );
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async unmarkAsDuplicate(postId: string) {
    try {
      const { data } = await this.axiosInstance.post(
        `/api/feed/posts/${postId}/unmark-duplicate`,
      );
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async searchPosts(searchTerm: string, type: PostType, excludeId?: string) {
    try {
      const params = new URLSearchParams({
        type,
        search: searchTerm,
        pageSize: '20',
        includeDuplicates: 'false',
      });

      const { data } = await this.axiosInstance.get(`/api/feed/posts?${params.toString()}`);
      const posts = data.posts || [];

      // Filter out the current post if excludeId is provided
      return excludeId ? posts.filter((p: any) => p.id !== excludeId) : posts;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
      return [];
    }
  }

  async getEvent(id: string) {
    try {
      const { data } = await this.axiosInstance.get(`/api/feed/posts/${id}`);
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async createEvent(payload: CreateEventPayload) {
    try {
      const { data } = await this.axiosInstance.post('/api/feed/posts', payload);
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async updateEvent(id: string, payload: Partial<CreateEventPayload>) {
    try {
      const { data } = await this.axiosInstance.put(`/api/feed/posts/${id}`, { ...payload, id });
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async deleteEvent(id: string) {
    try {
      await this.axiosInstance.delete(`/api/feed/posts/${id}`);
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
    }
  }

  async getCategories() {
    try {
      const { data } = await this.axiosInstance.get('/api/feed/categories');
      return data;
    } catch (e) {
      if ((e as AxiosError)?.response?.status !== 401) {
        throw e;
      }
      return [];
    }
  }
}

export const feedPostService = new FeedPostService();

// Keep legacy name for backward compatibility
export const eventService = feedPostService;
